import {
  IPersonDetails,
  IPersonParams,
  IUserDetailRequest,
  IUserDetailResponse,
  IUserListResponse,
  IUserParams,
} from 'core/model/interfaces/users';
import { formatDate } from 'shared/utils/date.utils';
import { removeEmptyValues } from 'shared/utils/object.utils';
import {
  IPersonDetailsDTO,
  IPersonParamsDTO,
  IUserDetailRequestDTO,
  IUserDetailResponseDTO,
  IUserListResponseDTO,
  IUserParamsDTO,
} from './users.dto';
import { getFullName } from './users.utils';

export const mapUserListDtoToDomain = ({ pagination, users }: IUserListResponseDTO): IUserListResponse => ({
  pagination,
  users: users.map(
    ({
      id,
      name,
      lastName,
      motherLastName,
      phone,
      document,
      customers,
      externalSupportTicketIds,
      legalConsentAcceptedAt,
      privacyConsentAcceptedAt,
    }) => ({
      id,
      fullName: getFullName(name, lastName, motherLastName),
      phone,
      document,
      customers: customers.map(({ id, externalId, isActive }) => ({
        id,
        externalId,
        isActive,
      })),
      supportTicketIds: externalSupportTicketIds,
      legalConsentAcceptedAt,
      privacyConsentAcceptedAt,
    }),
  ),
});

export const mapUserParamsDomainToDto = ({ ...rest }: IUserParams): IUserParamsDTO => ({
  ...rest,
});

export const mapUserDetailsDomainToDto = ({
  id,
  fullName,
  externalId,
  birthDate,
  isLegalConsentAccepted,
  isPrivacyConsentAccepted,
  phone,
  customers,
  supportTicketId,
  documentType,
  documentNumber,
}: IUserDetailRequest): IUserDetailRequestDTO =>
  removeEmptyValues({
    id,
    fullName,
    externalId,
    birthDate: birthDate && formatDate(birthDate),
    isLegalConsentAccepted,
    isPrivacyConsentAccepted,
    phone,
    customers: customers?.map(({ id, isActive }) => ({ id, isActive })),
    externalSupportTicketId: supportTicketId,
    documentType,
    documentNumber,
  });

export const mapUserDetailsDtoToDomain = ({
  id,
  name,
  lastName,
  motherLastName,
  phone,
  document,
  birthDate,
  email,
  legalConsentAcceptedAt,
  privacyConsentAcceptedAt,
  externalSupportTicketIds,
  customers,
  errors,
}: IUserDetailResponseDTO): IUserDetailResponse => ({
  id,
  fullName: getFullName(name, lastName, motherLastName),
  phone,
  document,
  email,
  errors,
  birthDate: birthDate && formatDate(birthDate, 'DD [de] MMMM YYYY'),
  legalConsentAcceptedAt: legalConsentAcceptedAt && formatDate(legalConsentAcceptedAt, 'DD/MM/YYYY - HH:mm A'),
  privacyConsentAcceptedAt: privacyConsentAcceptedAt && formatDate(privacyConsentAcceptedAt, 'DD/MM/YYYY - HH:mm A'),
  supportTicketIds: externalSupportTicketIds,
  customers:
    customers && customers.length
      ? customers.map(({ id, externalId, isActive, distributor, priceList, priceGroup, addresses }) => ({
          id,
          externalId,
          isActive,
          distributor: distributor?.name,
          priceList: priceList?.name,
          priceGroup: priceGroup?.name,
          address: addresses.find(({ isMain }) => isMain)?.address,
        }))
      : [
          {
            id: 0,
            externalId: undefined,
            isActive: undefined,
            distributor: undefined,
            priceList: undefined,
            priceGroup: undefined,
            address: undefined,
          },
        ],
});

export const mapPersonParamsDomainToDto = ({ ...rest }: IPersonParams): IPersonParamsDTO => ({
  ...rest,
});

export const mapPersonDetailsDtoToDomain = ({ name, lastName, motherLastName }: IPersonDetailsDTO): IPersonDetails => ({
  fullName: getFullName(name, lastName, motherLastName),
});
